
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  saveProfile,
  changePassword,
  verifyOldPassword,
} from "../api/profile";
import "../assets/styles/Auth.css";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import useAppLogout from "../hooks/useAppLogout";
import { FaUserEdit, FaKey, FaEye, FaEyeSlash } from "react-icons/fa";

export default function Profile() {
  const navigate = useNavigate();

  const [storedUser, setStoredUser] = useState(null);
  const [mode, setMode] = useState("view");
  const [isDirty, setIsDirty] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);

  /* ---------------- PROFILE ---------------- */

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile: "",
    alt_mobile: "",
  });

  /* ---------------- PASSWORD ---------------- */

  const [passwords, setPasswords] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });

  // Keep the latest values available to asynchronous callbacks.
  const passwordsRef = useRef(passwords);

  const [showPwd, setShowPwd] = useState({
    old_password: false,
    new_password: false,
    confirm_password: false,
  });

  const [pwdTouched, setPwdTouched] = useState({
    old_password: false,
    new_password: false,
    confirm_password: false,
  });

  const [oldPasswordError, setOldPasswordError] = useState("");
  const [checkingOldPassword, setCheckingOldPassword] = useState(false);
  const [oldPasswordVerified, setOldPasswordVerified] = useState(false);

  // Prevent stale verification responses from changing the current state.
  const verificationRequest = useRef(0);

  // Invalidate verification when leaving the password form.
  const resetPasswordState = () => {
    verificationRequest.current += 1;

    const emptyPasswords = {
      old_password: "",
      new_password: "",
      confirm_password: "",
    };

    passwordsRef.current = emptyPasswords;
    setPasswords(emptyPasswords);

    setPwdTouched({
      old_password: false,
      new_password: false,
      confirm_password: false,
    });

    setShowPwd({
      old_password: false,
      new_password: false,
      confirm_password: false,
    });

    setOldPasswordError("");
    setCheckingOldPassword(false);
    setOldPasswordVerified(false);
    setUpdatingPassword(false);
  };

  useAppLogout(isDirty, () => setShowLogoutPopup(true));

  /* ---------------- LOAD USER ---------------- */

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");

      if (!user || !user.id) {
        navigate("/");
        return;
      }

      const safeUser = {
        id: user.id,
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        mobile: String(user.mobile || ""),
        alt_mobile: String(user.alt_mobile || ""),
      };

      setStoredUser(safeUser);
      setForm(safeUser);
    } catch (error) {
      console.error("Unable to load profile:", error);
      navigate("/");
    }
  }, [navigate]);

  const doLogout = () => {
    // Preserve your existing logout behavior.
    localStorage.clear();
    navigate("/");
  };

  /* ---------------- PROFILE VALIDATION ---------------- */

  const errors = useMemo(() => {
    if (mode !== "profile") return {};

    const e = {};

    if (!form.first_name.trim()) {
      e.first_name = "First name required";
    }

    if (!form.last_name.trim()) {
      e.last_name = "Last name required";
    }

    const mobile = String(form.mobile || "").trim();
    const altMobile = String(form.alt_mobile || "").trim();

    if (!mobile) {
      e.mobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(mobile)) {
      e.mobile = "Enter a valid 10-digit mobile number";
    }

    if (altMobile && !/^[6-9]\d{9}$/.test(altMobile)) {
      e.alt_mobile = "Enter a valid 10-digit alternate mobile number";
    }

    if (mobile && altMobile && mobile === altMobile) {
      e.alt_mobile =
        "Alternate mobile number cannot be the same as primary mobile number";
    }

    return e;
  }, [form, mode]);

  /* ---------------- PROFILE UPDATE ---------------- */

  const updateField = (field, value) => {
    if (field === "mobile" || field === "alt_mobile") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }

    const updated = { ...form, [field]: value };

    setForm(updated);

    if (storedUser) {
      const changed =
        updated.first_name !== storedUser.first_name ||
        updated.last_name !== storedUser.last_name ||
        updated.mobile !== storedUser.mobile ||
        updated.alt_mobile !== storedUser.alt_mobile;

      setIsDirty(changed);
    }
  };

  const handleSave = async () => {
    if (!storedUser || savingProfile) return false;

    if (Object.keys(errors).length > 0) return false;

    setSavingProfile(true);

    try {
      const res = await saveProfile({
        id: storedUser.id,
        first_name: form.first_name,
        last_name: form.last_name,
        mobile: form.mobile,
        alt_mobile: form.alt_mobile,
      });

      if (res.status === "success") {
        const updatedUser = {
          ...storedUser,
          first_name: form.first_name,
          last_name: form.last_name,
          mobile: form.mobile,
          alt_mobile: form.alt_mobile,
        };

        localStorage.setItem("user", JSON.stringify(updatedUser));

        setStoredUser(updatedUser);
        setForm(updatedUser);
        setMode("view");
        setIsDirty(false);

        toast.success("Profile updated");
        return true;
      }

      toast.error(res.message || "Unable to update profile");
      return false;
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Unable to update profile"
      );
      return false;
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCancel = () => {
    if (storedUser) {
      setForm({ ...storedUser });
    }

    setMode("view");
    setIsDirty(false);
  };

  /* ---------------- BACK BUTTON ---------------- */

  const handleBack = () => {
    if (mode === "profile") {
      if (storedUser) {
        setForm({ ...storedUser });
      }

      setIsDirty(false);
    }

    if (mode === "password") {
      resetPasswordState();
    }

    setMode("view");
  };

  const saveAndLogout = async () => {
    if (savingProfile) return;

    const saved = await handleSave();

    if (saved) {
      setShowLogoutPopup(false);
      doLogout();
    }
  };

  /* ---------------- PASSWORD VALIDATION ---------------- */

  const pwdErrors = useMemo(() => {
    if (mode !== "password") return {};

    const e = {};
    const touched = Object.values(pwdTouched).some(Boolean);

    if (!touched) return e;

    if (!passwords.old_password) {
      e.old_password = "Old password required";
    }

    if (!passwords.new_password) {
      e.new_password = "New password required";
    } else if (passwords.new_password.length < 6) {
      e.new_password = "Minimum 6 characters";
    } else if (
      passwords.old_password &&
      passwords.new_password === passwords.old_password
    ) {
      e.new_password = "New password cannot be the same as old password";
    }

    if (!passwords.confirm_password) {
      e.confirm_password = "Confirm password required";
    } else if (passwords.confirm_password !== passwords.new_password) {
      e.confirm_password = "Passwords do not match";
    }

    return e;
  }, [passwords, pwdTouched, mode]);

  const isPwdValid =
    mode === "password" &&
    Object.values(pwdTouched).some(Boolean) &&
    Object.keys(pwdErrors).length === 0;

  /* ---------------- VERIFY OLD PASSWORD ---------------- */

  const verifyEnteredOldPassword = async () => {
    const oldPassword = passwordsRef.current.old_password;
    const requestId = ++verificationRequest.current;

    setOldPasswordError("");
    setOldPasswordVerified(false);

    if (!oldPassword || !storedUser?.id) {
      setCheckingOldPassword(false);
      return;
    }

    setCheckingOldPassword(true);

    try {
      const res = await verifyOldPassword({
        id: storedUser.id,
        old_password: oldPassword,
      });

      // Ignore a response if the password changed or another
      // verification request has started.
      if (
        requestId !== verificationRequest.current ||
        oldPassword !== passwordsRef.current.old_password
      ) {
        return;
      }

      if (res.status === "success") {
        setOldPasswordVerified(true);
        setOldPasswordError("");
      } else {
        setOldPasswordVerified(false);
        setOldPasswordError(
          res.message || "Old password is incorrect"
        );
      }
    } catch (error) {
      if (
        requestId !== verificationRequest.current ||
        oldPassword !== passwordsRef.current.old_password
      ) {
        return;
      }

      setOldPasswordVerified(false);
      setOldPasswordError(
        error.response?.data?.message ||
          "Unable to verify old password. Please try again."
      );
    } finally {
      if (requestId === verificationRequest.current) {
        setCheckingOldPassword(false);
      }
    }
  };

  const updatePassword = (field, value) => {
    // Update the ref immediately so pending requests see the latest value.
    const updatedPasswords = {
      ...passwordsRef.current,
      [field]: value,
    };

    passwordsRef.current = updatedPasswords;
    setPasswords(updatedPasswords);

    setPwdTouched((prev) => ({
      ...prev,
      [field]: true,
    }));

    if (field === "old_password") {
      // Invalidate a verification for the previous password.
      verificationRequest.current += 1;

      setOldPasswordError("");
      setOldPasswordVerified(false);
      setCheckingOldPassword(false);
    }
  };

  /* ---------------- PASSWORD UPDATE ---------------- */

  const handlePasswordUpdate = async () => {
    if (!isPwdValid || updatingPassword) return;

    if (checkingOldPassword) {
      toast.error("Please wait while the old password is verified");
      return;
    }

    if (!oldPasswordVerified || oldPasswordError) {
      setOldPasswordError("Please enter and verify your old password");
      toast.error("Please verify your old password first");
      return;
    }

    // Recheck before submission, including against the current input values.
    if (
      passwords.new_password === passwords.old_password
    ) {
      toast.error("New password cannot be the same as old password");
      return;
    }

    setUpdatingPassword(true);

    try {
      const res = await changePassword({
        id: storedUser.id,
        ...passwords,
      });

      if (res.status === "success") {
        toast.success("Password updated");
        resetPasswordState();
        setMode("view");
      } else {
        toast.error(res.message || "Unable to update password");
      }
    } catch (error) {
      const message =
        error.response?.data?.message || "Unable to update password";

      if (message.toLowerCase().includes("old password")) {
        setOldPasswordVerified(false);
        setOldPasswordError(message);
      }

      toast.error(message);
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handlePwdCancel = () => {
    resetPasswordState();
    setMode("view");
  };

  if (!storedUser) return null;

  return (
    <div>
      <Toaster position="top-right" />

      <div className="topbar">
        <h2>My Profile</h2>

        <div className="actions">
          {mode === "view" && (
            <>
              <button
                type="button"
                className="btn btn-edit"
                onClick={() => setMode("profile")}
              >
                <FaUserEdit className="btn-icon" /> Edit Profile
              </button>

              <button
                type="button"
                className="btn btn-password"
                onClick={() => {
                  resetPasswordState();
                  setMode("password");
                }}
              >
                <FaKey className="btn-icon" /> Change Password
              </button>
            </>
          )}

          {mode !== "view" && (
            <button
              type="button"
              className="btn btn-back"
              onClick={handleBack}
            >
              Back
            </button>
          )}
        </div>
      </div>

      {/* PROFILE FORM */}

      <div className="ticket-form">
        <div className="grid-2">
          {["first_name", "last_name"].map((field) => (
            <div className="field" key={field}>
              <label>
                {field === "first_name" ? "First Name" : "Last Name"} *
              </label>

              <input
                className={
                  mode === "profile" ? "input-edit" : "input-disabled"
                }
                value={form[field]}
                disabled={mode !== "profile"}
                onChange={(e) => updateField(field, e.target.value)}
              />

              {mode === "profile" && errors[field] && (
                <small className="error">{errors[field]}</small>
              )}
            </div>
          ))}

          <div className="field full">
            <label>Email</label>
            <input value={form.email} disabled />
          </div>

          <div className="field">
            <label>Mobile *</label>
            <input
              type="tel"
              inputMode="numeric"
              className={
                mode === "profile" ? "input-edit" : "input-disabled"
              }
              value={form.mobile}
              disabled={mode !== "profile"}
              maxLength={10}
              onChange={(e) => updateField("mobile", e.target.value)}
            />

            {mode === "profile" && errors.mobile && (
              <small className="error">{errors.mobile}</small>
            )}
          </div>

          <div className="field">
            <label>Alternate Mobile</label>
            <input
              type="tel"
              inputMode="numeric"
              className={
                mode === "profile" ? "input-edit" : "input-disabled"
              }
              value={form.alt_mobile}
              disabled={mode !== "profile"}
              maxLength={10}
              onChange={(e) => updateField("alt_mobile", e.target.value)}
            />

            {mode === "profile" && errors.alt_mobile && (
              <small className="error">{errors.alt_mobile}</small>
            )}
          </div>
        </div>
      </div>

      {mode === "profile" && (
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-cancel"
            onClick={handleCancel}
            disabled={savingProfile}
          >
            Cancel
          </button>

          <button
            type="button"
            className="btn btn-save"
            onClick={handleSave}
            disabled={
              Object.keys(errors).length > 0 ||
              !isDirty ||
              savingProfile
            }
          >
            {savingProfile ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}

      {/* PASSWORD UI */}

      {mode === "password" && (
        <div className="ticket-form" style={{ marginTop: 30 }}>
          <h3>Change Password</h3>

          {[
            "old_password",
            "new_password",
            "confirm_password",
          ].map((field) => (
            <div className="field full chang-pwd" key={field}>
              <label>
                {field === "old_password"
                  ? "Old Password"
                  : field === "new_password"
                  ? "New Password"
                  : "Confirm Password"}{" "}
                *
              </label>

              <div className="password-wrapper">
                <input
                  type={showPwd[field] ? "text" : "password"}
                  className="input-edit"
                  value={passwords[field]}
                  onChange={(e) => updatePassword(field, e.target.value)}
                  onBlur={
                    field === "old_password"
                      ? verifyEnteredOldPassword
                      : undefined
                  }
                  name={field}
                  autoComplete={
                    field === "old_password"
                      ? "current-password"
                      : "new-password"
                  }
                  autoCapitalize="none"
                  spellCheck={false}
                />

                <span
                  className="eye"
                  role="button"
                  tabIndex={0}
                  aria-label={
                    showPwd[field] ? "Hide password" : "Show password"
                  }
                  onClick={() =>
                    setShowPwd((prev) => ({
                      ...prev,
                      [field]: !prev[field],
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setShowPwd((prev) => ({
                        ...prev,
                        [field]: !prev[field],
                      }));
                    }
                  }}
                >
                  {showPwd[field] ? <FaEye /> : <FaEyeSlash />}
                </span>
              </div>

              {field === "old_password" && checkingOldPassword && (
                <small>Checking old password...</small>
              )}

              {field === "old_password" && oldPasswordError && (
                <small className="error">{oldPasswordError}</small>
              )}

              {field === "old_password" &&
                oldPasswordVerified &&
                !checkingOldPassword && (
                  <small className="success-message">
                    Old password verified
                  </small>
                )}

              {pwdErrors[field] && (
                <small className="error">{pwdErrors[field]}</small>
              )}
            </div>
          ))}

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={handlePwdCancel}
              disabled={updatingPassword}
            >
              Cancel
            </button>

            <button
              type="button"
              className="primary-btn"
              disabled={
                !isPwdValid ||
                checkingOldPassword ||
                !oldPasswordVerified ||
                Boolean(oldPasswordError) ||
                updatingPassword
              }
              onClick={handlePasswordUpdate}
            >
              {updatingPassword ? "Updating..." : "Update Password"}
            </button>
          </div>
        </div>
      )}

      {/* LOGOUT POPUP */}

      {showLogoutPopup && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>Unsaved Changes</h3>
            <p>Save before logout?</p>

            <div className="modal-actions">
              <button
                type="button"
                className="success-btn"
                onClick={saveAndLogout}
                disabled={savingProfile}
              >
                Save &amp; Logout
              </button>

              <button
                type="button"
                className="danger-btn"
                onClick={doLogout}
              >
                Discard
              </button>

              <button
                type="button"
                className="secondary-btn"
                onClick={() => setShowLogoutPopup(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
